import React from "react";
import dynamic from "next/dynamic";
import { NextSeo } from "next-seo";
import Head from "next/head";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import ContentfulApi from "@utils/ContentfulApi";
import getRelatedArticles from "utils/ContentfulApi/getRelatedArticles";
import FormEmbed from "@components/Reusables/FormEmbed";

import { parseDate, getArticleCardHref, sizeEqualOrLessThan } from "utils/helpers";
import { colors } from "utils/styles";
import Title from "@components/Reusables/Title";
import Text from "@components/Reusables/Text";
import DynamicLink from "@components/Reusables/DynamicLink";
import MarkdownContent from "components/Reusables/MarkdownContent";
import ArticleBody from "components/Reusables/ArticleBody";
import useJournalAuth from "hooks/useJournalAuth";
import { ContenfulQuickLink } from "components/DebugTools";
import Badge from "components/Badge";

import LibraryFilterBar from "components/Reusables/FilterBar/LibraryFilterBar";
import SocialShareIcons from "components/SocialShareIcons";

export default function DoiArticle({ article, doiPrefix, doiSuffix, relatedArticles }) {
	const { sys, fields } = article;

	const { ShowArticleAccessDenied, articleAccessClass } = useJournalAuth(fields);

	const { author, journalEdition = {}, endPage, abstract, publicationDate, startPage, title } = fields;

	const { journalEditionVolume, journalEditionIssue, editionDate } = journalEdition?.fields || {};
	const _pubDate = editionDate ? new Date(editionDate).toISOString() : publicationDate;
	const { dayOfWeek, dayOfMonth, month, year } = parseDate(_pubDate);

	const bannerImage = article?.fields.bannerImage?.fields?.file?.url
		? `https:${article?.fields.bannerImage?.fields?.file?.url}`
		: "https://images.ctfassets.net/mdag2z2o3c18/73533DQHZgyUDdrQ0j4iwo/d2ac1fd3ae5f15103e3ce6f107e3d7cb/aapl-logo.png";
	return (
		<>
			<NextSeo
				title={article.fields.summary || article.fields.abstract}
				description={article.fields.metaDescription}
				openGraph={{
					images: [
						{
							url: bannerImage,
						},
					],
					url: `${process.env.WEB_URL}${getArticleCardHref(article.fields)}`,
					title: article.fields.title,
					description: article.fields.summary || article.fields.abstract,
				}}
			/>
			<Head>
				<meta property="twitter:title" content={article.fields.summary || article.fields.abstract} />
				<meta name="twitter:image" content={bannerImage} />
				<meta name="twitter:description" content={article.fields.summary || article.fields.abstract} />
			</Head>

			<Container fluid className="  mx-auto relative ">
				{process.env.APP_ENV !== "production" && <ContenfulQuickLink entryId={sys.id} IR={true} />}

				<Container>
					{/* <Container className="g-0"> */}
					{/* <Row className="g-0"> */}
					{/* <Col> */}
					<LibraryFilterBar />
					{/* </Col> */}
					{/* </Row> */}
					{fields.peerReview && (
						<Col xs={"auto"}>
							<Badge title={"Peer-Reviewed"} label={"Peer-Reviewed"} color={"red"} fontSize="15" />
						</Col>
					)}
					<Title fontSize={"39px"} color={colors["darkBlue"]} text={title} />
					<Row className="align-items-center">
						<Col xs={"auto"}>
							<SocialShareIcons article={article} />
						</Col>{" "}
						<Col className="ms-0 ms-md-5">
							<Row className="mb-4">
								<Col>
									{author?.length &&
										author?.map((author) => {
											return (
												<React.Fragment key={author.sys.id}>
													<JournalText fontStyle="italic" text={author.fields.name} />
													<br />
												</React.Fragment>
											);
										})}
								</Col>
							</Row>
							<JournalText text={`${month} ${dayOfMonth}, ${year}`} />
							<br />
							{journalEdition && journalEdition.fields?.journal?.fields?.title && (
								<>
									<JournalText text={`${journalEdition.fields?.journal?.fields?.title}`} />
									<br />
								</>
							)}
							{journalEditionVolume !== undefined && journalEditionIssue !== undefined && (
								<JournalText
									text={`Volume ${journalEditionVolume}, Issue ${journalEditionIssue}, Pages ${startPage}-${endPage}`}
								/>
							)}
							<br />
							<JournalText
								color={colors.green}
								text={
									<a href={`https://doi.org/${doiPrefix}/${doiSuffix}`}>
										https://doi.org/{doiPrefix}/{doiSuffix}
									</a>
								}
							/>
							<br />
							<JournalText text={`Abstract`} className="my-4" weight="bold" color={colors["dark"]} />
							<Row>
								<Col md="8">
									<MarkdownContent
										style={{ color: colors["lightBrown"], fontSize: "16px", marginBottom: "0px" }}
										className="summary"
										markdown={abstract}
									/>
								</Col>
							</Row>
						</Col>
					</Row>
					<br />
					<br />
					<hr />
					{fields.formId !== undefined && (
						<FormEmbed
							data={{
								fields: {
									horizontalAlign: "center",
									formClasses: "d-flex flex-column p-4  radius-5px",
									containerClasses: "d-flex  w-100 p-1 p-sm-2 p-md-4",
									portalId: "2735001",
									formId: fields.formId,
								},
							}}
						/>
					)}

					<Row className="g-0">
						<Col className="px-3 px-lg-0" md={8}>
							<ArticleBody fields={fields} articleAccessClass={articleAccessClass} />
						</Col>
						<Col md={{ offset: 1, span: 3 }} className="d-none d-md-block">
							<Text text="Topics" weight="bold" size="18" marginBottom="0" />
							{fields.primaryTag && (
								<Text
									size="18"
									color={colors.green}
									className="m-0 d-flex"
									marginBottom="0"
									text={fields.primaryTag.title}
								/>
							)}
							{fields.secondaryTag && (
								<Text
									size="18"
									color={colors.green}
									className="m-0 d-flex"
									marginBottom="0"
									text={fields.secondaryTag.title}
								/>
							)}
							{fields.tertiaryTag && (
								<Text
									size="18"
									color={colors.green}
									className="m-0 d-flex"
									marginBottom="0"
									text={fields.tertiaryTag.title}
								/>
							)}
							{/* {childTags?.items?.length > 0 &&
										childTags?.items?.map((tag) => {
											return (
												<Text
													key={tag.title}
													size="18"
													color={colors.green}
													text={tag.title}
													className="m-0 d-flex justify-content-"
													marginBottom="0"
												/>
											);
										})} */}
							<br />
							<Text text="Related" weight="bold" size="18" marginBottom="0" />
							{relatedArticles?.length > 0 &&
								relatedArticles?.slice(0, 3).map((article) => {
									return (
										<DynamicLink
											key={article.canonicalUrl}
											classes="d-block mb-4"
											href={getArticleCardHref(article)}
											text={article.title}
											color={colors.dark}
											textDecoration="underline"
										/>
									);
								})}
						</Col>
						{!ShowArticleAccessDenied && (
							<Col className="px-3 px-lg-0" md={{ offset: 1, span: 9 }}>
								<Title text="Recommended Reading" element="h3" />
								<Row className="">
									{relatedArticles?.length > 0 &&
										relatedArticles?.map((article) => {
											return (
												<Col key={article.canonicalUrl} sm={6} className="mb-4">
													<Text
														weight="bold"
														size="16"
														color={colors.green}
														text={article.primaryTag}
														className="m-0 d-flex"
														marginBottom="0"
													/>
													<DynamicLink
														// textDecoration="none"
														href={getArticleCardHref(article)}
													>
														<Text
															// weight="bold"
															size="16"
															// color={colors.green}
															text={article.title}
															className="m-0 d-flex"
															marginBottom="0"
														/>
													</DynamicLink>
												</Col>
											);
										})}
								</Row>
							</Col>
						)}
					</Row>
				</Container>
				{ShowArticleAccessDenied}
			</Container>
		</>
	);
}

const JournalLink = (props) => {
	const defaultProps = {
		color: colors["green"],
		noMargin: true,
	};
	return <DynamicLink {...defaultProps} {...props} />;
};
const JournalText = (props) => {
	const defaultProps = {
		color: colors["lightBrown"],
		size: "16px",
		marginBottom: "0px",
	};
	return <Text {...defaultProps} {...props} />;
};

export async function getStaticPaths() {
	let articleDoiSlugs = await ContentfulApi.getAllDoiSuffixSlugs();

	//variable to hold the paths
	const paths = [];
	//getting each path
	articleDoiSlugs.forEach((item) => {
		const doiSuffix = item?.doiSuffix;
		const doiPrefix = item?.doiPrefix?.prefix;
		if (doiPrefix && doiSuffix) {
			paths.push({ params: { doiPrefix, doiSuffix } });
		}
	});

	return {
		paths,
		fallback: "blocking",
	};
}

export async function getStaticProps({ params }) {
	const { doiPrefix, doiSuffix } = params;
	const ignorePublishDate = true;
	const article = await ContentfulApi.getJournalArticle(doiSuffix, ignorePublishDate);

	if (!article) {
		return {
			notFound: true,
			...(process.env.REVALIDATE_TIMER && { revalidate: process.env.REVALIDATE_TIMER }),
		};
	}

	let relatedArticles = await getRelatedArticles(article);

	return {
		props: {
			article,
			doiPrefix,
			doiSuffix,
			relatedArticles,
		},
		...(process.env.REVALIDATE_TIMER && { revalidate: process.env.REVALIDATE_TIMER }),
	};
}
